import type { ClaudeMdResponse, ClaudeMdRevision } from '@omni/shared';
import { useEffect, useMemo, useState } from 'react';
import { isApiError } from '../api/client';
import { Badge, Button, Card, ErrorText, Spinner } from '../components/ui';
import { zh } from '../i18n';
import { formatBytes, formatDateTime } from '../lib/format';
import { diffLines } from './diff';
import { lintClaudeMd } from './claudeMdLint';

/**
 * 工作守則 editor (SPEC R25, CONTRACTS §4.5).
 *
 * The load-bearing behaviour is the optimistic lock. The PUT carries
 * `expectedSha256`; if the agent (or another tab) rewrote the file meanwhile the
 * server answers `409 STALE`, and this editor's contract at that moment is: say
 * so in 繁中, offer 重新載入, and **keep the user's draft in the textarea**.
 * Silently reloading over an unsaved edit would destroy the only work the UI is
 * allowed to create.
 *
 * Data and mutations arrive as props so the conflict path is testable without a
 * server (`test/claudeMdEditor.test.tsx`).
 */

export interface ClaudeMdEditorProps {
  readonly doc: ClaudeMdResponse | null;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly saveError: unknown;
  readonly onSave: (input: { content: string; expectedSha256?: string }) => void;
  readonly onReload: () => void;
  readonly revisions: readonly ClaudeMdRevision[] | null;
  readonly revisionsLoading: boolean;
  readonly onLoadRevisions: () => void;
  readonly onRestore: (revisionId: string) => void;
  readonly restoring: boolean;
  readonly restored: boolean;
}

const AUTHOR_LABEL: Record<ClaudeMdRevision['author'], string> = {
  user: zh.claudeMd.authorUser,
  agent: zh.claudeMd.authorAgent,
  seed: zh.claudeMd.authorSeed,
};

export function ClaudeMdEditor({
  doc,
  loading,
  saving,
  saveError,
  onSave,
  onReload,
  revisions,
  revisionsLoading,
  onLoadRevisions,
  onRestore,
  restoring,
  restored,
}: ClaudeMdEditorProps): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);

  // Adopt server content only while the user has no draft of their own.
  useEffect(() => {
    if (doc && draft === null) setDraft(doc.content);
  }, [doc, draft]);

  const content = draft ?? doc?.content ?? '';
  const baseline = doc?.content ?? '';
  const dirty = content !== baseline;
  const issues = useMemo(() => lintClaudeMd(content), [content]);
  const diff = useMemo(
    () => (showDiff ? diffLines(baseline, content) : []),
    [showDiff, baseline, content],
  );
  const stale = isApiError(saveError, 'STALE');

  if (loading && !doc) return <Spinner />;

  return (
    <div className="stack">
      <Card>
        <div className="row row--between">
          <h2>{zh.claudeMd.title}</h2>
          {dirty ? <Badge tone="warn">{zh.claudeMd.unsaved}</Badge> : null}
        </div>
        <p className="small muted">{zh.claudeMd.subtitle}</p>

        {stale ? (
          <div className="banner banner--warn" role="alert">
            <span className="banner__text">
              {zh.claudeMd.stale}
              <br />
              <span className="tiny">{zh.claudeMd.staleKeepDraft}</span>
            </span>
            <Button
              variant="outline"
              small
              onClick={() => {
                setDraft(null);
                onReload();
              }}
            >
              {zh.claudeMd.reload}
            </Button>
          </div>
        ) : (
          <ErrorText error={saveError} />
        )}

        <textarea
          className="editor"
          aria-label={zh.claudeMd.title}
          value={content}
          spellCheck={false}
          onChange={(event) => setDraft(event.target.value)}
        />

        <div className="row" style={{ marginTop: '0.5rem' }}>
          <Button
            variant="primary"
            disabled={saving || !dirty}
            onClick={() =>
              onSave({
                content,
                ...(doc?.sha256 ? { expectedSha256: doc.sha256 } : {}),
              })
            }
          >
            {saving ? zh.app.saving : zh.app.save}
          </Button>
          <Button variant="ghost" onClick={() => setShowDiff((value) => !value)}>
            {showDiff ? zh.claudeMd.hideDiff : zh.claudeMd.preview}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setShowRevisions((value) => !value);
              if (!showRevisions) onLoadRevisions();
            }}
          >
            {zh.claudeMd.revisions}
          </Button>
          <span className="spacer" />
          <span className="tiny faint">
            {doc ? `${formatBytes(new TextEncoder().encode(content).length)}` : ''}
          </span>
        </div>
      </Card>

      <Card>
        <h3>{zh.claudeMd.lintTitle}</h3>
        {issues.length === 0 ? (
          <p className="small muted">{zh.claudeMd.lintClean}</p>
        ) : (
          <ul className="stack stack--sm" style={{ paddingInlineStart: '1.1rem' }}>
            {issues.map((issue) => (
              <li key={issue.id} className={issue.level === 'error' ? 'field__error' : 'small muted'}>
                {issue.message}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {showDiff ? (
        <Card>
          <h3>{zh.claudeMd.preview}</h3>
          {diff.length === 0 ? (
            <p className="small muted">{zh.claudeMd.diffNoChange}</p>
          ) : (
            <div className="diff">
              {diff.map((line, index) => (
                <div
                  key={`${index}-${line.text}`}
                  className={
                    line.type === 'add'
                      ? 'diff__line diff__line--add'
                      : line.type === 'remove'
                        ? 'diff__line diff__line--del'
                        : 'diff__line'
                  }
                >
                  <span className="diff__mark" aria-hidden="true">
                    {line.type === 'add' ? '+' : line.type === 'remove' ? '-' : ' '}
                  </span>
                  {line.type === 'context' ? null : (
                    <span className="sr-only">
                      {line.type === 'add' ? zh.claudeMd.diffAdded : zh.claudeMd.diffRemoved}
                    </span>
                  )}
                  <span>{line.text}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}

      {showRevisions ? (
        <Card>
          <h3>{zh.claudeMd.revisionsTitle}</h3>
          {restored ? <p className="small muted">{zh.claudeMd.restored}</p> : null}
          {revisionsLoading ? <Spinner /> : null}
          {revisions && revisions.length > 0 ? (
            <table className="table">
              <tbody>
                {revisions.map((revision) => (
                  <tr key={revision.id}>
                    <td>{formatDateTime(revision.createdAt)}</td>
                    <td>{AUTHOR_LABEL[revision.author]}</td>
                    <td>{formatBytes(revision.sizeBytes)}</td>
                    <td>
                      <Button
                        variant="ghost"
                        small
                        disabled={restoring}
                        onClick={() => {
                          if (window.confirm(zh.claudeMd.restoreConfirm)) {
                            setDraft(null);
                            onRestore(revision.id);
                          }
                        }}
                      >
                        {restoring ? zh.claudeMd.restoring : zh.claudeMd.restore}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : revisionsLoading ? null : (
            <p className="small muted">{zh.app.empty}</p>
          )}
        </Card>
      ) : null}
    </div>
  );
}
